import { db } from '@/mocks/db';
import { mapAttendanceRecordToDailyRow } from '../-shared/api/mapAttendanceRecordToDailyRow';
import { normalizeAttendanceRecordForDemo } from '../-shared/api/normalizeAttendanceRecordForDemo';
import { CLOCKING_RECORDS_DEMO_ORG_ID, CLOCKING_RECORDS_FALLBACK_YEAR_MONTH } from '../-shared/constants';
import type { ClockingRecordsDailyRow } from '../-shared/types';
import { computeEmployeeAttendanceBookSummary, type EmployeeAttendanceBookSummary } from './computeAttendanceBookSummary';
export type { EmployeeAttendanceBookSummary };
export type EmployeeClockingProfile = {
  employeeId: string;
  employeeName: string;
  department: string;
  workStyleName: string;
  avatarUrl: string | null;
};
function monthPrefix(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * 打刻または日次勤怠から、当該従業員の最新年月を推定する。
 */
export async function getEmployeeLatestClockingPeriodFromDb(employeeId: string): Promise<{
  year: number;
  month: number;
} | null> {
  const records = await db.clockingRecords.where('employeeId').equals(employeeId).toArray();
  let best: {
    y: number;
    m: number;
  } | null = null;
  for (const r of records) {
    const d = new Date(r.timestamp);
    if (Number.isNaN(d.getTime())) continue;
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    if (!best || y > best.y || y === best.y && m > best.m) {
      best = {
        y,
        m
      };
    }
  }
  if (best) {
    return {
      year: best.y,
      month: best.m
    };
  }
  const daily = await db.attendanceRecords.filter(r => r.employeeId === employeeId && r.organizationId === CLOCKING_RECORDS_DEMO_ORG_ID).toArray();
  for (const r of daily) {
    if (r.date.length < 7) continue;
    const y = Number(r.date.slice(0, 4));
    const m = Number(r.date.slice(5, 7));
    if (!Number.isFinite(y) || !Number.isFinite(m)) continue;
    if (!best || y > best.y || y === best.y && m > best.m) {
      best = {
        y,
        m
      };
    }
  }
  if (best) {
    return {
      year: best.y,
      month: best.m
    };
  }
  const [fy, fm] = CLOCKING_RECORDS_FALLBACK_YEAR_MONTH.split('-').map(Number);
  if (Number.isFinite(fy) && Number.isFinite(fm)) {
    return {
      year: fy,
      month: fm
    };
  }
  return null;
}
export async function getEmployeeClockingProfile(employeeId: string): Promise<EmployeeClockingProfile | null> {
  const row = await db.attendanceEmployees.where('employeeId').equals(employeeId).first();
  if (!row) return null;
  const legacy = row as typeof row & {
    workStyleName?: string;
  };
  return {
    employeeId: row.employeeId,
    employeeName: row.employeeName,
    department: row.department,
    workStyleName: row.attendanceStructureName ?? legacy.workStyleName ?? '',
    avatarUrl: null
  };
}
export type EmployeeAttendanceBookBundle = {
  rows: ClockingRecordsDailyRow[];
  summary: EmployeeAttendanceBookSummary;
};

/**
 * 出勤簿タブ用: 指定従業員・年月の日次行と月次サマリ（Dexie デモ）
 */
export async function getEmployeeAttendanceBookBundle(employeeId: string, year: number, month: number): Promise<EmployeeAttendanceBookBundle> {
  const prefix = monthPrefix(year, month);
  const raw = await db.attendanceRecords.filter(r => r.employeeId === employeeId && r.organizationId === CLOCKING_RECORDS_DEMO_ORG_ID && r.date.startsWith(prefix)).toArray();
  const normalized = raw.map(normalizeAttendanceRecordForDemo);
  const sorted = [...normalized].sort((a, b) => a.date.localeCompare(b.date));
  const summary = computeEmployeeAttendanceBookSummary(sorted, year, month);
  const rows = sorted.map(mapAttendanceRecordToDailyRow);
  return {
    rows,
    summary
  };
}

/**
 * 出勤簿タブ(チーム/店舗 scope)用: 指定 employeeId 群・年月の日次行を集約。
 * person 列は portal 表示のため、staff 詳細ではなく人物プロフィール `/person/$id` へリンクする。
 */
export async function getScopeAttendanceBook(employeeIds: string[], year: number, month: number): Promise<ClockingRecordsDailyRow[]> {
  const prefix = monthPrefix(year, month);
  // employeeId はインデックス済み。anyOf で先に走査範囲を絞ってから org / 年月で filter する。
  const raw = await db.attendanceRecords.where('employeeId').anyOf(employeeIds).filter(r => r.organizationId === CLOCKING_RECORDS_DEMO_ORG_ID && r.date.startsWith(prefix)).toArray();
  const normalized = raw.map(normalizeAttendanceRecordForDemo);
  const rows = normalized.map(record => {
    const row = mapAttendanceRecordToDailyRow(record);
    return row.person ? {
      ...row,
      person: {
        ...row.person,
        linkTo: `/person/${row.person.id}`
      }
    } : row;
  });

  // 日付昇順 → 同日内は氏名で安定ソート。
  rows.sort((a, b) => {
    const da = a.date.getTime();
    const dbt = b.date.getTime();
    if (da !== dbt) return da - dbt;
    return (a.person?.name ?? '').localeCompare(b.person?.name ?? '');
  });
  return rows;
}
export type ScopeMemberOvertime = {
  employeeId: string;
  name: string;
  overtimeMinutes: number;
  workMinutes: number;
};
export type ScopeAttendanceOverview = {
  memberCount: number;
  /** scope 全体の集約サマリ（self では本人のサマリと一致） */
  summary: EmployeeAttendanceBookSummary;
  /** メンバー別 残業時間（降順）。概要の残業ランキング表示用 */
  members: ScopeMemberOvertime[];
};

/**
 * 概要タブ用: scope の集約サマリ＋メンバー別残業ランキング。
 */
export async function getScopeAttendanceOverview(employeeIds: string[], year: number, month: number): Promise<ScopeAttendanceOverview> {
  const prefix = monthPrefix(year, month);
  // employeeId はインデックス済み。anyOf で先に走査範囲を絞ってから org / 年月で filter する。
  const raw = await db.attendanceRecords.where('employeeId').anyOf(employeeIds).filter(r => r.organizationId === CLOCKING_RECORDS_DEMO_ORG_ID && r.date.startsWith(prefix)).toArray();
  const normalized = raw.map(normalizeAttendanceRecordForDemo);

  // scope 全体の集約サマリ（全レコードで計算 → 平日数 denominator は当月固定）
  const summary = computeEmployeeAttendanceBookSummary(normalized, year, month);

  // メンバー別に集約して残業ランキングを作る
  const byEmployee = new Map<string, typeof normalized>();
  for (const r of normalized) {
    const list = byEmployee.get(r.employeeId);
    if (list) list.push(r);else byEmployee.set(r.employeeId, [r]);
  }
  const members: ScopeMemberOvertime[] = [];
  for (const [employeeId, records] of byEmployee) {
    const memberSummary = computeEmployeeAttendanceBookSummary(records, year, month);
    members.push({
      employeeId,
      name: records[0]?.employeeName ?? employeeId,
      overtimeMinutes: memberSummary.totalOvertimeMinutes,
      workMinutes: memberSummary.totalWorkMinutes
    });
  }
  members.sort((a, b) => b.overtimeMinutes - a.overtimeMinutes);
  return {
    memberCount: byEmployee.size,
    summary,
    members
  };
}